import { IPartner } from "api/types/_news";
import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownOption from "components/Common/DropdownOption";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { filterMediaOptions, formatHour, formatNumberWithCommas, formatNumberWithoutCommas } from "helpers/format";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Input, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getAllCategory, getAllPartners, hourlyStatistics as onGetHourlyStatistics } from "../../../store/thunks";
import ChartLine from "./ChartLine";
import { ICategory } from "api/types/_category";


const SORT_BY_DEFAULT = 'hour';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const ICON_COMPARISON: any = {
    up: '▲',
    down: '▼',
    equal: '='
};

const CLASS_COMPARISON: any = {
    up: 'fs-11 text-danger',
    down: 'fs-11 text-primary',
    equal: 'fs-13 text-muted',
};

const getClassComparison = (num: string | number) => {
    if (Number(num) > 0) {
        return 'up';
    }
    if (Number(num) < 0) {
        return 'down';
    }
    return 'equal';
}

const HourlyStatistic = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        start_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        end_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        compare_start_date: withDefault(StringParam, `${moment().subtract(1, 'days').format("Y-MM-DD")}`),
        compare_end_date: withDefault(StringParam, `${moment().subtract(1, 'days').format("Y-MM-DD")}`),
        partner_id: withDefault(StringParam, ''),
        category_id: withDefault(StringParam, ''),
        article_title: withDefault(StringParam, ''),
        article_urls: withDefault(StringParam, ''),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Statistic;

    const StatisticProperties = createSelector(
        selectLayoutState,
        (state) => ({
            hourly: state.hourly,
            isHourlySuccess: state.isHourlySuccess,
            isHourlyLoading: state.isHourlyLoading,
            error: state.error,
        })
    );

    const { hourly, isHourlyLoading } = useSelector(StatisticProperties);
    const [isCallAllOptionLoading, setIsCallAllOptionLoading] = useState<boolean>(true);

    const [dateSearch, setDateSearch] = useState<any[]>([query?.start_date ? moment(query?.start_date, 'Y-MM-DD').toDate() : null, query?.end_date ? moment(query?.end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;

    const [dateComparisonSearch, setDateComparisonSearch] = useState<any[]>([query?.compare_start_date ? moment(query?.compare_start_date, 'Y-MM-DD').toDate() : null, query?.compare_end_date ? moment(query?.compare_end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDateComparison, endDateComparison] = dateComparisonSearch;

    const [mediaSearch, setMediaSearch] = useState<Option | null>(!!query?.partner_id ? { value: query?.partner_id, label: '' } : null);

    const [categorySearch, setCategorySearch] = useState<Option | null>(!!query?.category_id ? { value: query?.category_id, label: '' } : null);

    const [partners, setPartners] = useState<IPartner[]>([]);

    const [categories, setCategories] = useState<ICategory[]>([]);

    const [titleSearch, setTitleSearch] = useState<string>(query?.article_title || "");
    const [urlsSearch, setUrlsSearch] = useState<string>((query?.article_urls || "").replace(/,/g, '\n'));

    useEffect(() => {
        dispatch(onGetHourlyStatistics(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            compare_start_date: startDateComparison ? moment(new Date(startDateComparison)).format("Y-MM-DD") : '',
            compare_end_date: endDateComparison ? moment(new Date(endDateComparison)).format("Y-MM-DD") : '',
            partner_id: mediaSearch?.value || '',
            category_id: categorySearch?.value || '',
            article_title: titleSearch || '',
            article_urls: (urlsSearch || '').replace(/\n/g, ','),
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            start_date: `${moment().format("Y-MM-DD")}`,
            end_date: `${moment().format("Y-MM-DD")}`,
            compare_start_date: `${moment().subtract(1, 'days').format("Y-MM-DD")}`,
            compare_end_date: `${moment().subtract(1, 'days').format("Y-MM-DD")}`,
            partner_id: '',
            category_id: '',
            article_title: '',
            article_urls: '',
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setTitleSearch("");
        setUrlsSearch("");
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
        setDateComparisonSearch([moment(queryNew?.compare_start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.compare_end_date || "", 'Y-MM-DD').toDate()]);
        setMediaSearch({ label: t('All'), value: '' });
        setCategorySearch({ label: t('All'), value: '' });
    };


    const calculateStats = useMemo(() => {
        let data = hourly?.list || [];
        let stats: any = {
            impression_min: Number.MAX_SAFE_INTEGER,
            impression_max: Number.MIN_SAFE_INTEGER,
            click_min: Number.MAX_SAFE_INTEGER,
            click_max: Number.MIN_SAFE_INTEGER,
            ctr_min: Number.MAX_SAFE_INTEGER,
            ctr_max: Number.MIN_SAFE_INTEGER,
        };

        data.forEach((item: any) => {

            // Update impressions
            const impression = formatNumberWithoutCommas(item.impression_sum);
            if (impression < stats.impression_min) stats.impression_min = impression;
            if (impression > stats.impression_max) stats.impression_max = impression;

            // Update clicks
            const click = formatNumberWithoutCommas(item.click_sum);
            if (click < stats.click_min) stats.click_min = click;
            if (click > stats.click_max) stats.click_max = click;

            // Update CTR
            const ctr = parseFloat(item.ctr_sum);
            if (ctr < parseFloat(stats.ctr_min)) stats.ctr_min = ctr.toFixed(2);
            if (ctr > parseFloat(stats.ctr_max)) stats.ctr_max = ctr.toFixed(2);
        });

        // Convert numbers back to string with necessary formatting
        stats.impression_min = formatNumberWithCommas(stats.impression_min)?.toString();
        stats.impression_max = formatNumberWithCommas(stats.impression_max)?.toString();
        stats.click_min = formatNumberWithCommas(stats.click_min)?.toString();
        stats.click_max = formatNumberWithCommas(stats.click_max)?.toString();
        stats.ctr_min = formatNumberWithCommas(stats.ctr_min)?.toString();
        stats.ctr_max = formatNumberWithCommas(stats.ctr_max)?.toString();
        return stats;
    }, [hourly?.list]);

    const formatAccumulatedHourRange = () => {
        // Get the current time in KST
        const now = moment.tz("Asia/Seoul");
        const currentHour = now.hours();
        let startHour = '00:00';
        let endHour;
        if (currentHour === 0) {
            endHour = '00:00';
        } else {
            let endHourValue = currentHour;
            endHour = endHourValue.toString().padStart(2, '0') + ":00";
        }

        return `${startHour} ~ ${endHour}`;
    };

    const isCurrentTimeInRange = (timeRange: string = '') => {
        if (!timeRange.includes(' ~ ')) return false;
        const now = moment.tz("Asia/Seoul");
        const n = now.hours() * 60 + now.minutes();
        const [s, e] = timeRange.split(' ~ ').map((x): number =>
            x.split(':').reduce((h, m) => h * 60 + +m, 0)
        );
        return n >= s && n < e;
    }

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Time'),
                accessor: "hour",
                filterable: true,
                sortable: false,
                thWidth: 190,
                thClass: 'align-middle text-start',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const classIsCurrentTime = (!item?.text_cell && !!cell?.value && isCurrentTimeInRange(formatHour(cell?.value))) ? 'row-border-primary' : '';
                    return (
                        !!item?.text_row ? <strong>{item?.text_row} </strong> :
                            <div className={`text-start ${classIsCurrentTime} ${item?.text_cell ? 'row-bg-warning' : ''}`} style={{ minWidth: '170px' }}>
                                <span>{item?.text_cell && <>{item?.text_cell || ''} <br /></>}{item?.is_accumulated ? (formatAccumulatedHourRange()) : formatHour(cell?.value)}</span>
                            </div>
                    )
                },
            },
            // {
            //     Header: t('Request'),
            //     accessor: "request",
            //     filterable: true,
            //     sortable: false,
            //     thClass: 'align-middle text-end',
            //     thWidth: 160,
            //     thComponent: () => (
            //         <div className="text-center d-inline-block">
            //             {t('Request')}
            //             <br />
            //             <span className="fw-normal">({t('Comparison')})</span>
            //         </div>
            //     ),
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         const classN = !!item?.text_row ? 'text-end' : `text-end ${calculateStats?.request_min === cell?.value ? 'td-bg-primary' : (calculateStats?.request_max === cell?.value ? 'td-bg-danger' : '')}`;
            //         return (
            //             <>
            //                 <div className={classN} style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
            //                 {<div className="text-end" style={{ minWidth: '100px' }}>(<span className={CLASS_COMPARISON[item?.request_compare] || ''}>{ICON_COMPARISON[item?.request_compare] || ''} <span className="fs-13">{formatNumberWithCommas(item?.value_request_change)}</span></span>)</div>}
            //             </>
            //         )
            //     },
            // },
            {
                Header: t('Impression'),
                accessor: "impression_sum",
                filterable: true,
                sortable: false,
                thClass: 'align-middle text-end',
                thWidth: 160,
                thComponent: () => (
                    <div className="text-center d-inline-block">
                        {t('Impression')}
                        <br />
                        <span className="fw-normal">({t('Comparison')})</span>
                    </div>
                ),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const classN = !!item?.text_row ? 'text-end' : `text-end ${calculateStats?.impression_min === String(formatNumberWithCommas(cell?.value)) ? 'td-bg-primary' : (calculateStats?.impression_max === String(formatNumberWithCommas(cell?.value)) ? 'td-bg-danger' : '')}`;
                    return (
                        <>
                            <div className={classN} style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                            {<div className="text-end" style={{ minWidth: '100px' }}>(<span className={CLASS_COMPARISON[getClassComparison(item?.compare_impression_sum)] || ''}>{ICON_COMPARISON[getClassComparison(item?.compare_impression_sum)] || ''} <span className="fs-13">{formatNumberWithCommas(Math.abs(item?.compare_impression_sum))}</span></span>)</div>}
                        </>
                    )
                },
            },
            {
                Header: t('Click'),
                accessor: "click_sum",
                filterable: true,
                sortable: false,
                thClass: 'align-middle text-end',
                thWidth: 160,
                thComponent: () => (
                    <div className="text-center d-inline-block">
                        {t('Click')}
                        <br />
                        <span className="fw-normal">({t('Comparison')})</span>
                    </div>
                ),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const classN = !!item?.text_row ? 'text-end' : `text-end ${calculateStats?.click_min === String(formatNumberWithCommas(cell?.value)) ? 'td-bg-primary' : (calculateStats?.click_max === String(formatNumberWithCommas(cell?.value)) ? 'td-bg-danger' : '')}`;
                    return (
                        <>
                            <div className={classN} style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                            {<div className="text-end" style={{ minWidth: '100px' }}>(<span className={CLASS_COMPARISON[getClassComparison(item?.compare_click_sum)] || ''}>{ICON_COMPARISON[getClassComparison(item?.compare_click_sum)] || ''} <span className="fs-13">{formatNumberWithCommas(Math.abs(item?.compare_click_sum))}</span></span>)</div>}
                        </>
                    )
                },
            },
            {
                Header: t('CTR'),
                accessor: "ctr_sum",
                filterable: true,
                sortable: false,
                thClass: 'align-middle text-end',
                thWidth: 160,
                thComponent: () => (
                    <div className="text-center d-inline-block">
                        {t('CTR')}
                        <br />
                        <span className="fw-normal">({t('Comparison')})</span>
                    </div>
                ),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const classN = !!item?.text_row ? 'text-end' : `text-end ${Number(calculateStats?.ctr_min) === Number(cell?.value) ? 'td-bg-primary' : (calculateStats?.ctr_max === String(formatNumberWithCommas(cell?.value)) ? 'td-bg-danger' : '')}`;
                    return (
                        <>
                            <div className={classN} style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                            {<div className="text-end" style={{ minWidth: '100px' }}>(<span className={CLASS_COMPARISON[getClassComparison(item?.compare_ctr_sum)] || ''}>{ICON_COMPARISON[getClassComparison(item?.compare_ctr_sum)] || ''} <span className="fs-13">{formatNumberWithCommas(Math.abs(Number(item?.compare_ctr_sum)))} %</span></span>)</div>}
                        </>
                    )
                },
            },
        ],
        [i18n?.language, userPermissions, JSON.stringify(calculateStats)]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    const handleChangePickerComparison = (values: any[] = []) => {
        setDateComparisonSearch((_prev: any) => values);
    }

    const handleClearUrlsSearch = () => {
        const queryNew = {
            ...query,
            article_urls: '',
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setUrlsSearch("");
    };

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                setIsCallAllOptionLoading((_prev) => true);
                const [res1, res2]: any = await Promise.all([getAllPartners(), getAllCategory()]);
                setIsCallAllOptionLoading((_prev) => false);
                setPartners((_prev: any) => res1?.data);
                setCategories((_prev: any) => res2?.data);
                if (query?.partner_id) {
                    const _partner = res1?.data?.find((_p: any) => String(_p?.id) === String(query?.partner_id));
                    setMediaSearch({ value: `${_partner?.id}`, label: _partner?.name });
                }
                if (query?.category_id) {
                    const _category = res2?.data?.find((_p: any) => String(_p?.id) === String(query?.category_id));
                    setCategorySearch({ value: `${_category?.id}`, label: _category?.name });
                }
            } catch (error: any) {
                setIsCallAllOptionLoading((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    const categories_chart = useMemo(() => {
        return hourly?.list?.map((item: any) => formatHour(item?.hour)) || [];
    }, [hourly?.list, i18n?.language]);

    const series_chart = useMemo(() => {
        const obChart = hourly?.list?.reduce((ob: any, item: any) => {
            ob.impression?.push(Number(String(item?.impression_sum).replace(/,/g, '')));
            ob.click?.push(Number(String(item?.click_sum).replace(/,/g, '')));
            ob.ctr?.push(Number(String(item?.ctr_sum).replace(/,/g, '')));
            return ob;
        }, { impression: [], click: [], ctr: [] }) || [];
        return (
            [
                {
                    name: t('CTR'),
                    type: 'line',
                    data: obChart?.ctr,
                },
                {
                    name: t('Click'),
                    type: 'line',
                    data: obChart?.click,
                },
                {
                    name: t('Impression'),
                    type: 'column',
                    data: obChart?.impression,
                },
            ]
        )
    }, [hourly?.list, i18n?.language]);

    const currentTime = useMemo(() => {
        return (hourly?.list || []).find((item: any) => isCurrentTimeInRange(formatHour(item?.hour)));
    }, [hourly?.list]);


    const totalArticleURL = useMemo(() => {
        return (urlsSearch || '')?.split('\n')?.filter((_url: string) => !!_url)?.length;
    }, [urlsSearch]);

    useEffect(() => {
        document.title = `${t('Hourly Statistics')} | ${t('PRESS Ai')}`;
        document.body.classList.remove('vertical-sidebar-enable');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Hourly Statistics')} pageTitle={t('Statistics')} />
                    {isHavePermissionRole(ROLES_FOR_APP.HOURLY_STATISTICS, userPermissions) && (
                        <Row>
                            <Col sm={12} md={12}>
                                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                    <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                    <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                        <Row className="g-4 align-items-center">
                                            <div className="col-sm">
                                                <div>
                                                    <h5 className="card-title mb-0">
                                                        <span className="me-2" style={{ color: 'transparent' }}>{t('Total')}</span>
                                                        {/* {(isHourlyLoading) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                                                            start={0}
                                                            end={hourly?.pagination?.total || 0}
                                                            duration={1}
                                                            className="text-primary"
                                                        />)} */}
                                                    </h5>
                                                </div>
                                            </div>
                                        </Row>
                                    </CardHeader>
                                    <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                        <Row className="g-4 align-items-center mt-0">
                                            <Col sm={6} md={6} xl={3} xxl={3} className='mt-3 mt-xl-2'>
                                                <LabelWrapper label={t('Media')} isShow={true}>
                                                    <DropdownOption
                                                        name="media"
                                                        dataList={filterMediaOptions(partners || [])?.map((_p: any) => ({ label: _p?.name, value: `${_p?.id}` }))}
                                                        placeholder={`${t("Media")}...`}
                                                        className="dropdown-status-rounded z-hight"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={mediaSearch || null}
                                                        onChangeSelect={(e: any) => setMediaSearch(e)}
                                                        isHasOptionAll={true}
                                                        optionAll={{ label: t('All'), value: '' }}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={6} md={6} xl={3} xxl={3} className='mt-3 mt-xl-2'>
                                                <LabelWrapper label={t('Category')} isShow={true}>
                                                    <DropdownOption
                                                        name="category"
                                                        dataList={(categories || [])?.map((_p: any) => ({ label: _p?.name, value: `${_p?.id}` }))}
                                                        placeholder={`${t("Category")}...`}
                                                        className="dropdown-status-rounded z-hight"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={categorySearch || null}
                                                        onChangeSelect={(e: any) => setCategorySearch(e)}
                                                        isHasOptionAll={true}
                                                        optionAll={{ label: t('All'), value: '' }}
                                                    />
                                                </LabelWrapper>
                                            </Col>

                                            <Col sm={6} md={6} xl={3} xxl={3} className='date-picker-wrapper-custom mt-3 mt-xl-2'>
                                                <LabelWrapper label={t('Comparison Date')} isShow={!!startDate || !!endDate}>
                                                    <DatePickerCustom
                                                        placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                        startDate={startDateComparison || null}
                                                        endDate={endDateComparison || null}
                                                        onChangePicker={handleChangePickerComparison}
                                                        showOptions={[
                                                            'today', 'yesterday', 'two_day_ago',
                                                            'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                            'this_week', 'last_week',
                                                            'this_month', 'last_month',
                                                        ]}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={6} md={6} xl={3} xxl={3} className='date-picker-wrapper-custom mt-3 mt-xl-2'>
                                                <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                                    <DatePickerCustom
                                                        placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                        startDate={startDate || null}
                                                        endDate={endDate || null}
                                                        onChangePicker={handleChangePicker}
                                                        showOptions={[
                                                            'today', 'yesterday', 'two_day_ago',
                                                            'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                            'this_week', 'last_week',
                                                            'this_month', 'last_month',
                                                        ]}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={12} md={6} xl={3} xxl={3} className='mt-3'>
                                                <LabelWrapper label={t('Article Title')} isShow={true}>
                                                    <Input
                                                        type="textarea"
                                                        rows={1}
                                                        className="form-control"
                                                        placeholder={`${t('Article Title')}...`}
                                                        value={titleSearch}
                                                        onChange={(e) => setTitleSearch(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={12} md={6} xl={9} xxl={9} className='mt-3 position-relative'>
                                                <LabelWrapper label={t('Article URLs')} isShow={true}>
                                                    <Input
                                                        type="textarea"
                                                        rows={1}
                                                        className="form-control"
                                                        placeholder={`${t('Multiple URLs can be entered at once when pasted, separated by line breaks')}...`}
                                                        value={urlsSearch}
                                                        onChange={(e) => setUrlsSearch(e.target.value)}
                                                    // onKeyDown={(e) => {
                                                    //     if (e.key === "Enter") {
                                                    //         searchData();
                                                    //     }
                                                    // }}
                                                    />
                                                </LabelWrapper>
                                                <span className="position-absolute fs-12 text-muted" style={{ bottom: '-18px' }}>{t('Total')}: {formatNumberWithCommas(totalArticleURL)} {t('items')}</span>
                                                {!!totalArticleURL && <button type="button" className="button-icon-clear" aria-label="Clear" onClick={handleClearUrlsSearch}></button>}
                                            </Col>
                                            <Col sm={12} md={12} xl={12} xxl={12} className="hstack gap-1 justify-content-center justify-content-sm-end justify-content-xl-end mt-3">
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary me-2 "
                                                        onClick={searchData}
                                                        disabled={(isHourlyLoading || isCallAllOptionLoading)}
                                                    >
                                                        {(isHourlyLoading || isCallAllOptionLoading) ? <div className="me-1" style={{ display: 'inline-table', verticalAlign: 'top' }}><Spinner size="sm" style={{ width: '14px', height: '14px' }}></Spinner></div> : <i className="ri-search-line align-bottom me-1"></i>}{" "}
                                                        {t('Button Search')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary fs-14"
                                                        onClick={resetData}
                                                    >
                                                        <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                        {t('Button Reset')}
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    <ChartLine
                                        className="my-3"
                                        titles={[t('CTR'), t('Click'), t('Impression')]}
                                        categories={categories_chart}
                                        series={series_chart}
                                    />
                                    <CardBody className="pt-3">
                                        <TableContainer
                                            className="custom-header-css"
                                            divClass="table-responsive table-card"
                                            tableClass="align-middle table-bordered-dashed table-have-first-row-is-total sticky-table-report-hourly-statistic"
                                            theadClass="table-light text-muted"
                                            columns={columns}
                                            data={hourly?.list?.length ? (
                                                [
                                                    {
                                                        ...hourly?.total,
                                                        // compare_impression_sum: hourly?.total?.value_impression_change === '0' ? 'equal' : hourly?.total?.compare_impression_sum,
                                                        // compare_click_sum: hourly?.total?.value_click_change === '0' ? 'equal' : hourly?.total?.compare_click_sum,
                                                        // compare_ctr_sum: hourly?.total?.value_ctr_change === '0.00' ? 'equal' : hourly?.total?.compare_ctr_sum,
                                                        domain: null,
                                                        text_row: t('Sum')
                                                    },
                                                    {
                                                        ...hourly?.accumulated,
                                                        // compare_impression_sum: hourly?.total_data_accumulated?.value_impression_change === '0' ? 'equal' : hourly?.total_data_accumulated?.compare_impression_sum,
                                                        // compare_click_sum: hourly?.total_data_accumulated?.value_click_change === '0' ? 'equal' : hourly?.total_data_accumulated?.compare_click_sum,
                                                        // compare_ctr_sum: hourly?.total_data_accumulated?.value_ctr_change === '0.00' ? 'equal' : hourly?.total_data_accumulated?.compare_ctr_sum,
                                                        domain: null,
                                                        is_accumulated: true,
                                                        text_cell: t('Accumulated Timezone')
                                                    },
                                                    {
                                                        ...currentTime,
                                                        text_cell: t('Current Timezone')
                                                    },
                                                    ...hourly?.list]
                                            ) : []}
                                            customPageSize={Number(hourly?.pagination?.total)}
                                            customPageIndex={1}
                                            totalRecords={hourly?.pagination?.total}
                                            customPageCount={Math.ceil(Number(hourly?.pagination?.total) / Number(hourly?.pagination?.total))}
                                            handleChangePage={handleChangePage}
                                            manualSorting={true}
                                            sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                            handleChangeSorting={handleChangeSorting}
                                            isLoading={isHourlyLoading}
                                            isShowPagination={false}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default HourlyStatistic;
